import React, { useMemo, useState } from 'react';
import { Copy, Download, Users } from 'lucide-react';
import EditSpeakersModal from '../EditSpeakersModal';
import MoreFormatsMenu from '../transcript/MoreFormatsMenu';
import {
  EXPORT_FORMATS, NO_TIMES_HINT, buildExport, canSubtitle, segmentsFromFragments,
} from '../../lib/transcriptExport';
import { formatDuration } from '../../utils/audioDuration';
import { formatTimecodeHMMSS } from '../../FormatTime';

/**
 * Фаза результата: расшифровка диалогом и рельс с тем, что о записи известно.
 *
 * Реплики показываются одинаково для всех источников, различается только
 * подпись говорящего: у стерео-звонка это канал и роль, у остальных — имя
 * участника.
 */

/**
 * Доли речи по говорящим.
 *
 * 🔴 Считаем по ВРЕМЕНИ, а не по числу реплик: одна длинная реплика весит
 * больше трёх коротких, и счёт по репликам показал бы неправду ровно там, где
 * на неё смотрят — «кто больше говорил» на разборе звонка.
 *
 * У потоковой записи границы фраз бывают пустыми: тогда долей нет вовсе, а не
 * NaN на экране.
 */
export function speechShares(fragments) {
  const totals = new Map();
  let sum = 0;
  fragments.forEach((f) => {
    const seconds = (typeof f.end === 'number' && typeof f.start === 'number')
      ? Math.max(0, f.end - f.start) : 0;
    const who = f.name || f.speaker || '—';
    totals.set(who, (totals.get(who) || 0) + seconds);
    sum += seconds;
  });
  if (!sum) return [];
  return [...totals.entries()]
    .map(([who, seconds]) => ({ who, pct: Math.round((seconds / sum) * 100) }))
    .sort((a, b) => b.pct - a.pct);
}

/**
 * Таймкод реплики для экрана: начало отрезка у диаризации и стерео, строка
 * «00:01:05» у плоского текста (файл без участников, ссылка) — у него чисел
 * start/end нет, есть только время из строки сервера, уже без миллисекунд.
 */
function turnTimecode(fragment) {
  if (typeof fragment.start === 'number') return formatTimecodeHMMSS(fragment.start);
  return fragment.timecode || null;
}

const Turn = ({ fragment, withChannel, showTimecodes = true }) => (
  <div className="grid grid-cols-[132px_1fr] gap-5">
    <div className="flex items-start gap-[10px] min-w-0">
      {withChannel && (
        <span className={`w-[22px] h-[22px] shrink-0 rounded-badge flex items-center justify-center
                          font-mono text-[11px] text-white ${
          fragment.channel === 'R' ? 'bg-paper-accent' : 'bg-paper-ink'}`}>
          {fragment.channel}
        </span>
      )}
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold text-paper-ink">
          {withChannel ? fragment.speaker : (fragment.name || fragment.speaker)}
        </span>
        {withChannel && fragment.name && (
          <span className="block truncate text-[12px] text-paper-ink-3">{fragment.name}</span>
        )}
        {showTimecodes && turnTimecode(fragment) && (
          <span className="block font-mono text-[11px] text-paper-hint">
            {turnTimecode(fragment)}
          </span>
        )}
      </span>
    </div>
    <p className="m-0 max-w-[62ch] text-[15px] leading-[1.65] text-paper-ink">{fragment.text}</p>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between text-[13px] text-paper-ink-2">
    <span>{label}</span>
    <b className="font-mono font-medium text-paper-ink">{value}</b>
  </div>
);

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3';

/** Плоский текст расшифровки — то, что уходит в буфер и в файл. */
export function plainText(fragments, withTimecodes = true) {
  return fragments.map((f) => [
    f.speaker ? `${f.speaker}:` : null,
    withTimecodes && turnTimecode(f) ? `[${turnTimecode(f)}]` : null,
    f.text,
  ].filter(Boolean).join(' ')).join('\n');
}

/** Склонение: «1 вопрос», «2 вопроса», «5 вопросов». */
function questionWord(n) {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 19) return 'вопросов';
  if (mod10 === 1) return 'вопрос';
  if (mod10 >= 2 && mod10 <= 4) return 'вопроса';
  return 'вопросов';
}

const ResultState = ({ fragments, kind, meta, onCreateProtocol, onNew, onDownload, onRename,
                       voice = [], onVoiceAnswer }) => {
  const withChannel = kind === 'stereo';
  const shares = speechShares(fragments);
  const [showTimecodes, setShowTimecodes] = useState(true);
  const [renaming, setRenaming] = useState(false);

  // У стерео-звонка переименовывать нечего: там роли каналов, а не участники.
  const canRename = !withChannel && fragments.some((f) => f.speaker);
  const speakers = [...new Set(fragments.map((f) => f.speaker).filter(Boolean))];

  const handleCopy = () => navigator.clipboard?.writeText(plainText(fragments, showTimecodes));

  // Субтитры, markdown, JSON — за стрелкой у «Скачать .txt» (обращение 6f371de1).
  // onDownload получает содержимое, расширение и тип файла; имя файла даёт экран.
  const segments = useMemo(() => segmentsFromFragments(fragments), [fragments]);
  const subtitlesOk = canSubtitle(segments);
  const formats = EXPORT_FORMATS.map((f) => ({ ...f, disabled: f.needsTimes && !subtitlesOk }));
  const handleExport = (key) => {
    if (!onDownload) return;
    const file = buildExport(key, { segments, title: meta.title || null,
                                    durationSec: meta.duration || null, showTimecodes });
    onDownload(file.content, file.ext, file.mime);
  };

  const ACTION = 'flex items-center gap-[6px] rounded-chip bg-paper-bg px-3 py-[6px] text-[13px] text-paper-ink hover:bg-paper-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent';
  const CHEVRON = 'rounded-chip rounded-l-none bg-paper-bg text-paper-ink hover:bg-paper-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent';

  return (
    <div className="flex-1 min-h-0 grid gap-3 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_292px] items-start">
      <div className="bg-paper-card rounded-card px-4 pt-5 pb-4 md:px-[34px] md:pt-[30px] md:pb-[18px] flex flex-col min-h-0">
        {/* Действия над текстом — там же, где текст. В артборде они уехали в
            верхнюю строку экрана; перенос туда меняет обязанности AppTopbar и
            каждого экрана разом, поэтому вынесен отдельной задачей
            (docs/backlog/redesign-shell-gaps.md, пункт 2). */}
        <div className="mb-5 flex flex-wrap items-center gap-2 shrink-0">
          <button type="button" onClick={() => setShowTimecodes(!showTimecodes)}
            id="result-timecodes" aria-label="Таймкоды" aria-pressed={showTimecodes}
            className={`rounded-chip px-3 py-[6px] text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent ${
              showTimecodes ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}>
            Таймкоды
          </button>

          {canRename && (
            <button type="button" onClick={() => setRenaming(true)} className={ACTION}>
              <Users size={14} strokeWidth={1.75} /> Имена участников
            </button>
          )}

          <button type="button" onClick={handleCopy} className={ACTION}>
            <Copy size={14} strokeWidth={1.75} /> Копировать
          </button>

          <MoreFormatsMenu items={formats} onPick={handleExport} disabledHint={NO_TIMES_HINT}
            chevronClassName={CHEVRON}>
            <button type="button" onClick={() => onDownload && onDownload(plainText(fragments, showTimecodes))}
              className={`${ACTION} rounded-r-none`}>
              {/* Формат в подписи: без него человек ждёт документ или субтитры,
                  а получает текст. */}
              <Download size={14} strokeWidth={1.75} /> Скачать .txt
            </button>
          </MoreFormatsMenu>
        </div>

        <div role="region" aria-label="Расшифровка"
             className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-5 pr-2">
          {fragments.map((fragment, index) => (
            <Turn key={index} fragment={fragment} withChannel={withChannel}
              showTimecodes={showTimecodes} />
          ))}
        </div>

        <EditSpeakersModal isOpen={renaming} onClose={() => setRenaming(false)}
          speakers={speakers}
          onSave={(map, remembered) => { setRenaming(false); if (onRename) onRename(map, remembered); }} />
      </div>

      <aside role="region" aria-label="О записи" className="flex flex-col gap-3">
        {shares.length > 0 && (
          <div className="bg-paper-card rounded-panel p-5">
            {/* Счётчик в заголовке — потому что сбоку вопрос легче пропустить,
                чем поперёк экрана. Пока на него не ответили, блок помечен. */}
            <div className={`mb-3 font-mono text-[11px] uppercase tracking-[0.1em] ${
              voice.length ? 'text-paper-accent' : 'text-paper-ink-3'}`}>
              {withChannel ? 'Каналы'
                : voice.length ? `Участники · ${voice.length} ${questionWord(voice.length)}`
                  : 'Участники'}
            </div>
            <div className="flex flex-col gap-2">
              {shares.map(({ who, pct }, index) => (
                <div key={who} className="flex items-center gap-[10px] text-[13px]">
                  <span className="w-[74px] shrink-0 truncate text-paper-ink">{who}</span>
                  <span className="h-[6px] flex-1 overflow-hidden rounded-[3px] bg-paper-bg">
                    <span
                      className={`block h-full rounded-[3px] ${index === 0 ? 'bg-paper-ink' : 'bg-paper-accent'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="w-[34px] text-right font-mono text-[12px] text-paper-ink-3">
                    {`${pct}%`}
                  </span>
                </div>
              ))}
            </div>

            {voice.length > 0 && (
              <div className="mt-3 flex flex-col gap-2 rounded-control bg-paper-panel p-3">
                {voice.map((item) => (
                  <div key={item.speakerId} className="text-[13px] leading-[1.5]">
                    <div className="text-paper-ink">
                      {item.kind === 'recognized'
                        ? `Узнали: ${item.name}`
                        : `Возможно, ${item.label} — это ${item.name}`}
                    </div>
                    <div className="mt-2 flex gap-[6px]">
                      <button type="button"
                        onClick={() => onVoiceAnswer && onVoiceAnswer('confirm', item)}
                        className="rounded-chip bg-paper-accent px-[10px] py-1 text-[12px] font-semibold text-white hover:bg-paper-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
                        {item.kind === 'recognized' ? 'Это он' : 'Да, это он'}
                      </button>
                      <button type="button"
                        onClick={() => onVoiceAnswer && onVoiceAnswer('reject', item)}
                        className="rounded-chip bg-paper-card px-[10px] py-1 text-[12px] text-paper-ink-2 hover:text-paper-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
                        {item.kind === 'recognized' ? 'Не он' : 'Нет'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="bg-paper-card rounded-panel p-5 flex flex-col gap-[6px]">
          <div className={`${LABEL} mb-1`}>Запись</div>
          {meta.duration ? <Row label="Длительность" value={formatDuration(meta.duration)} /> : null}
          {meta.date ? <Row label="Дата" value={meta.date} /> : null}
          {meta.model ? <Row label="Модель" value={meta.model} /> : null}
          {meta.type ? <Row label="Тип" value={meta.type} /> : null}
          <Row label="Реплик" value={String(fragments.length)} />
        </div>

        <button type="button" onClick={onCreateProtocol}
          className="rounded-control bg-paper-accent hover:bg-paper-accent-hover py-[14px] text-[14px] font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
          Сгенерировать протокол
        </button>
        <button type="button" onClick={onNew}
          className="rounded-control bg-paper-card hover:bg-paper-panel py-[14px] text-[14px] font-medium text-paper-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
          Новая запись
        </button>
        {/* Кнопка убирает расшифровку с экрана. В Историю она сохранена, но в
            момент нажатия человек об этом не думает — говорим прямо. */}
        <p className="m-0 text-center text-[12px] text-paper-ink-3">
          Эта расшифровка останется в Истории
        </p>
      </aside>
    </div>
  );
};

export default ResultState;
